import { envelopeCardOf } from "@/components/generative-ui/envelope";
import type { FormDraftCardEnvelope, FormDraftSetCardEnvelope } from "@/components/generative-ui/envelope/types";

import { isToolResultState, toolPartsOf, type MessageLike } from "./message-parts";
import type { DraftData } from "./types";

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function parseToolResult(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "string") return asRecord(value);

  try {
    return asRecord(JSON.parse(value));
  } catch {
    return null;
  }
}

export function resolveDraftState(
  toolName: string,
  toolInput: unknown,
  toolResult: unknown,
): Pick<DraftData, "status" | "payload"> {
  const result = parseToolResult(toolResult);
  const data = asRecord(result?.data);
  const input = asRecord(toolInput);
  const payload = { ...(data ?? input ?? {}) };

  if (toolName === "draft_stock_item" && !payload.idempotency_key) {
    const card = asRecord(result?.card);
    const cardId = card?.card_id;
    if (typeof cardId === "string" && cardId.length > 0) {
      payload.idempotency_key = cardId;
    }
  }

  const rawStatus = result?.status;
  const status: DraftData["status"] = rawStatus === "rejected"
    ? "rejected"
    : rawStatus === "approved" || rawStatus === "confirmed"
      ? "approved"
      : "drafting";

  return { status, payload };
}

function asFormDraftCard(value: unknown): FormDraftCardEnvelope | null {
  const card = asRecord(value);
  return card !== null && card.kind === "form.draft"
    ? (card as unknown as FormDraftCardEnvelope)
    : null;
}

/** The `form.draft` card a draft part carries, or null when it carries
 * another kind (a `confirmation`, which renders through the generic envelope).
 *
 * Two shapes answer: the envelope the draft tool emitted, and the approval
 * frame the web wrote back over it. The decision replaces the envelope in the
 * message store, so the frame keeps the card it approved (SCRUM-283) and a
 * remount reads the form summary here instead of a raw tool result.
 *
 * The envelope arrives as a JSON string or as an object — the copilot SSE
 * parses the `tool-output-available` output before it yields it, and its card
 * carries no `kind` of its own (the envelope holds `card_kind`). Both go
 * through `envelopeCardOf`, the same reader the generic branch uses, so the
 * two paths always agree on what a card is (SCRUM-285). */
export function formDraftCardOf(toolOutput: unknown): FormDraftCardEnvelope | null {
  const card = envelopeCardOf(toolOutput);
  if (card && card.kind === "form.draft") return card;

  return asFormDraftCard(parseToolResult(toolOutput)?.card);
}

/** The `form.draft.set` card a draft part carries (WEB-FORMSET-01), through
 * the same envelope reader as every other card, so a JSON string and an
 * already-parsed SSE object agree. There is no approval frame for a set: the
 * browser posts to Laravel and the thread never carries a decision. */
export function formDraftSetCardOf(toolOutput: unknown): FormDraftSetCardEnvelope | null {
  const card = envelopeCardOf(toolOutput);
  return card && card.kind === "form.draft.set" ? withSetDefaults(card) : null;
}

const SET_CONFIRM_LABEL = "Salvar e agendar";

/** The contract's optional-with-default keys, filled once here so no view has
 * to remember them: `envelopeCardOf` checks the kind, not the shape. */
function withSetDefaults(card: FormDraftSetCardEnvelope): FormDraftSetCardEnvelope {
  return {
    ...card,
    target_label: card.target_label ?? null,
    warnings: card.warnings ?? [],
    confirm_label: card.confirm_label ?? SET_CONFIRM_LABEL,
    attachments: card.attachments ?? [],
    elements: (card.elements ?? []).map((element) => ({
      ...element,
      refs: element.refs ?? [],
      server_owned: element.server_owned ?? [],
      warnings: element.warnings ?? [],
      fields: (element.fields ?? []).map((field) => ({ ...field, required: field.required ?? false, editable: field.editable ?? true })),
    })),
  };
}

/** A confirm the runtime refused, as `FormDraftCardView` reads it. */
export type FormDraftFailure = {
  userMessage: string;
  fields: Record<string, readonly string[]>;
};

const FORM_SAVE_REFUSED = "Não foi possível salvar. Corrija os campos marcados.";

/** The `TOOL_VALIDATION_ERROR` a confirm tool answers with, or null for any
 * other output. An outage answers `API_UNAVAILABLE` and is not a form state:
 * it is not something the clinic can fix in the fields
 * (docs/architecture/specs/copilot-form-draft-card.md §3.1). */
export function formValidationErrorOf(toolOutput: unknown): FormDraftFailure | null {
  const output = parseToolResult(toolOutput);
  if (output === null) return null;

  const error = asRecord(output.error) ?? output;
  // The runtime spells it `tool_validation_error`: `ErrorType` is a StrEnum, so
  // the envelope carries the value, not the member name the spec writes in
  // capitals. Comparing without case is what makes the failed state reachable.
  if (String(error.error_type).toLowerCase() !== "tool_validation_error") return null;

  const raw = asRecord(asRecord(error.details)?.fields) ?? {};
  const fields: Record<string, readonly string[]> = {};
  for (const [key, value] of Object.entries(raw)) {
    const messages = (Array.isArray(value) ? value : [value])
      .filter((message): message is string => typeof message === "string" && message.length > 0);
    if (messages.length > 0) fields[key] = messages;
  }

  const userMessage = typeof error.user_message === "string" && error.user_message.length > 0
    ? error.user_message
    : FORM_SAVE_REFUSED;

  return { userMessage, fields };
}

export type FormDraftFailures = {
  /** The refusal each approved draft carries, keyed by the draft's `toolCallId`. */
  byDraft: Map<string, FormDraftFailure>;
  /** The confirm parts whose refusal a form card already renders. The drawer
   * skips them so the clinic reads the message once, on the fields. */
  handledConfirms: Set<string>;
  /** Each refused confirm and the draft it belongs to (SCRUM-284). */
  refusedConfirms: Map<string, string>;
  /** The drafts whose confirm wrote. Approving one again writes nothing: the
   * runtime already retired the card. */
  writtenDrafts: Set<string>;
};

/** Which approved `form.draft` parts the runtime refused to write.
 *
 * The confirm runs inside the runtime and streams as its own tool part in a
 * later message, so the two are joined by the card's `action`: the refusal
 * belongs to the last draft approved into that confirm tool. A later confirm
 * that is not a validation error clears it — the write went through. */
export function formDraftFailures(messages: readonly MessageLike[]): FormDraftFailures {
  const byDraft = new Map<string, FormDraftFailure>();
  const handledConfirms = new Set<string>();
  const refusedConfirms = new Map<string, string>();
  const writtenDrafts = new Set<string>();
  const approvedByAction = new Map<string, string>();
  const approvedByCard = new Map<string, string>();

  for (const message of messages) {
    for (const tool of toolPartsOf(message)) {
      if (tool.toolName.startsWith("draft_")) {
        const card = formDraftCardOf(tool.output);
        const status = parseToolResult(tool.output)?.status;
        if (card && status === "approved") {
          approvedByAction.set(card.action, tool.toolCallId);
          approvedByCard.set(card.card_id, tool.toolCallId);
        }
        continue;
      }

      if (!isToolResultState(tool.state)) continue;

      // The confirm carries the id of the card it is writing (SCRUM-284), so
      // two drafts of the same action in one thread stay apart. The action is
      // the fallback for a confirm that predates the id.
      const cardId = asRecord(tool.input)?.card_id;
      const draftCallId = (typeof cardId === "string" ? approvedByCard.get(cardId) : undefined)
        ?? approvedByAction.get(tool.toolName);
      if (draftCallId === undefined) continue;

      const failure = formValidationErrorOf(tool.output);
      if (failure) {
        byDraft.set(draftCallId, failure);
        handledConfirms.add(tool.toolCallId);
        refusedConfirms.set(tool.toolCallId, draftCallId);
        continue;
      }

      byDraft.delete(draftCallId);
      // An outage clears the form state without retiring the card: it is not
      // something the clinic fixes in the fields, and the write never happened.
      const output = parseToolResult(tool.output);
      if (output?.has_error !== true && output?.error_type === undefined) {
        writtenDrafts.add(draftCallId);
      }
    }
  }

  return { byDraft, handledConfirms, refusedConfirms, writtenDrafts };
}

/** The approval payload of the form card: the draft's own `data` with the
 * edited keys replaced. Server-owned keys are not in `values` (they have no
 * field), so they travel unchanged. */
export function mergeFormDraftValues(
  payload: Record<string, unknown>,
  values: Record<string, string | null>,
  serverOwned: readonly string[] = [],
): Record<string, unknown> {
  const merged = { ...payload };
  for (const [key, value] of Object.entries(values)) {
    if (serverOwned.includes(key)) continue;
    merged[key] = value;
  }
  return merged;
}
